import { TimeoutError } from './../custom-errors/timeout.error';
import { NavigationError, Router, RoutesRecognized } from '@angular/router';
import { ApolloError } from '@apollo/client/errors';
import { Platform } from '@angular/cdk/platform';
import { Inject, Injectable, Injector, LOCALE_ID, OnDestroy, Optional, TransferState, makeStateKey } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Location } from '@angular/common';
import { UsersService } from 'app/shared/services/users/users.service';
import { ErrorContext } from './error-context.model';
// Cool library to deal with errors: https://www.stacktracejs.com
// import * as StackTraceParser from 'error-stack-parser';
import type { LocalForageService } from 'app/shared/services/local-forage/local-forage.service';
import { LoggingService } from './logging.service';
import { EMPTY, filter, pairwise, tap } from 'rxjs';
import { PageNotFoundError } from '../custom-errors/page-not-found.error';
import { GraphQLError } from 'graphql';
import { ApolloNetworkError } from '../custom-errors/apollo-network-error';
import { NotAuthorizedError } from '../custom-errors/not-authorized-error';

// import crypto from 'crypto';

declare const navigator: any;
export const ERRORS_STORE = 'errors_store';

export const ERROR_STATUS = makeStateKey<ErrosStatus>('error_status');

export class ErrosStatus {
  statusCode: number;
  url: string;
}

@Injectable({
    providedIn: 'root'
})
export class ErrorService  implements OnDestroy {
  private get localForageService(): Promise<LocalForageService> {
      return (async () => {
        if (!this._localForageService) {
          const { LocalForageService } = await import('app/shared/services/local-forage/local-forage.service');
          const injector = Injector.create({
            providers: [{ provide: LocalForageService, useClass: LocalForageService }],
            parent: this.injector,
          });
          this._localForageService = injector.get(LocalForageService);
        }
        return this._localForageService;
      })();
  }
  private _localForageService: LocalForageService;

  private get browserNavigator(): any {
    if (this.platform.isBrowser) {
      if (!navigator.connection) navigator.connection = {};
      return navigator;
    } else {
      return {language: this.locale, platform: 'server-side', userAgent: 'omitted', connection: {}}
    }
  }

  private prevUrl: string;
  private curUrl: string;
  private userService: UsersService;
  private loggingService: LoggingService;

  constructor(
    private platform: Platform,
    private location: Location,
    private tstate: TransferState,
    @Inject(LOCALE_ID) private locale: string,
    private injector: Injector,
    private router: Router
  ){
    if (this.platform.isBrowser) {

      this.userService = injector.get(UsersService);
      this.loggingService = injector.get(LoggingService);
      this.router.events
      .pipe(
        filter((evt: any) => evt instanceof RoutesRecognized),
        tap(ev => this.curUrl = ev.urlAfterRedirects),
        pairwise())
      .subscribe((events: RoutesRecognized[]) => {
        this.prevUrl = events[0].urlAfterRedirects;
        this.curUrl = events[1].urlAfterRedirects;
      });

      const errorStatus = this.tstate.get(ERROR_STATUS, null);

      if(errorStatus) {   
        this.navigateError(errorStatus.statusCode, errorStatus.url);
      }

    } else {
      this.router.events
      .subscribe(e => {
        if (e instanceof NavigationError) {
          if(e.error instanceof PageNotFoundError) {
            this.navigateError(e.error.statusCode, e.url);
          } else {
            this.navigateError(500, e.url);
          }
          
        }
      });
    }

  }

  async logErrors(): Promise<void> {
    const localForageService = await this.localForageService;
    const errors = await localForageService.getAll(ERRORS_STORE);
    if (errors && errors.length > 0) {
      this.loggingService.logErrors(errors)
      .subscribe({
        next: () => {
          localForageService.clearAll(ERRORS_STORE);
        },
        error: error => {
          console.error('An error occurred during log operation!', error);
        }
      });
    }

  }

  async handleError(error: Error): Promise<any> {
    if(error.name === 'HttpErrorResponse') {
      return EMPTY;
    }
    let errorContext: ErrorContext, localForageService: LocalForageService, stack, graphQlErrors = [], status: number = -1;
    const url = this.curUrl ||  this.router.getCurrentNavigation()?.extractedUrl.toString() || this.router.url || '';
    if (url.match(/\/error\/[0-9]{3}/)) {
      return EMPTY;
    }

    // if (networkError && networkError.name === 'Timeout') {
    //   const { statusCode } = networkError as TimeoutError;
    //   status = statusCode;
    //   error = networkError;
    // } else if (networkError && networkError.name === 'HttpErrorResponse') {
    //   await this.navigateError(503, url);
    //   return EMPTY;
    // } else if(networkError && networkError.name === 'HttpErrorResponse') {

    // }


    if (this.isGraphQLError(error)) {
        const code = error.extensions?.code || 'none';
        const graphqlError = error;
        switch (code) {
          case "SLUG_NOT_FOUND":
            const notFound = new PageNotFoundError(url);
              status = notFound.statusCode;
              error = notFound;
          case "PAGE_CURSOR_NOT_FOUND":
            const pageCursorFound = new PageNotFoundError(url);
              status = pageCursorFound.statusCode;
              error = pageCursorFound;
          default:
          graphQlErrors.push(graphqlError);
        }
    } else if (error instanceof ApolloNetworkError) {
      status = error.status;
      await this.navigateError(503, url);
      return EMPTY;
    } else if (error instanceof HttpErrorResponse) {
      status = error.status;
    }

    if(error instanceof PageNotFoundError || error instanceof TimeoutError  || error instanceof NotAuthorizedError) {
      await this.navigateError(error.statusCode, url);
    }
    const userid = this.userService?.currentUserId || null;

    const message = error.message ? this.parseMessage(error.message) : JSON.stringify(error); //fix: googlebot da errore perché error.message è undefined, da controllare.

    const {language, platform, userAgent, connection: { downlink, effectiveType}} = this.browserNavigator;
    let id = '';

    if (this.platform.isBrowser) {
      id = await this.digestMessage(`err-${language}-${platform}-${userAgent}-${message}`);
      localForageService = await this.localForageService;
      errorContext = await localForageService.getItem(ERRORS_STORE, id);
    } else {
      id = `err-${language}-${platform}-${userAgent}-${message}`
    }

    if (errorContext) {
      errorContext.errorCount++;
      errorContext.time = new Date().getTime();
    } else {
      if (!['Timeout', 'PageNotFound', 'NotAuthorized'].includes(error.name)) {

        const StackTraceParser = await import('error-stack-parser').then(s => s.default);
        try {
          stack = StackTraceParser.parse(error);
        } catch (err) {
          console.log(err.message);
        }
      }

      errorContext = {
        id: id,
        browserLanguage: language,
        platform: platform,
        userAgent: userAgent,
        connectionDownlink: downlink || null,
        connectionEffectiveType: effectiveType || null,
        lang: this.locale,
        name: error.name || null,
        userId: userid || null,
        time: new Date().getTime(),
        url: url,
        message: message,
        stack: stack,
        graphQlErrors: graphQlErrors,
        status: status,
        fromUrl: this.prevUrl,
        errorCount: 1,
      };
    }
    if (this.platform.isBrowser) {
      localForageService.save(ERRORS_STORE, id, errorContext);
    } else {
      this.loggingService?.logErrors([errorContext])
      .subscribe();
    }

    return EMPTY;
  }

  navigateError(statusCode: number, url: string): void {
    this.router.navigate([`/error/${statusCode}`], { skipLocationChange: true, queryParams: {returl: url} });
    if (this.platform.isBrowser) {
      this.location.replaceState(url);
    } else {
      this.tstate.set<ErrosStatus>(ERROR_STATUS, {statusCode: statusCode, url: url});
    }
  }

  private parseMessage(message: string): string {
    // first line of message
    const regex = new RegExp('(?:.*[^\n])');
    let parsed: string;
    if (regex.test(message)) {
      parsed = message.match(regex)[0];
    } else {
      parsed = message;
    }

    return parsed;
  }

  private isGraphQLError(err: unknown): err is GraphQLError {
    if (!err) return false
    if (!Object.prototype.hasOwnProperty.call(err, 'path')) return false
    if (!Object.prototype.hasOwnProperty.call(err, 'extensions')) return false
    return true
  }

  async digestMessage(message) {
    const msgUint8 = new TextEncoder().encode(message);// encode as (utf-8) Uint8Array
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8);           // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer));                     // convert buffer to byte array
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join(''); // convert bytes to hex string
  }

  async ngOnDestroy(): Promise<void> {
    if(this.platform.isBrowser) {
      try {
        await this.logErrors();
      }
      catch(err) {
        // ignore
      }
    }
    
  }
}
